import { z } from 'zod'
import { Currency } from '../types/enums'

/**
 * Satın Alma / Alınan Teklifler (teklif toplama & kıyaslama) şemaları.
 * Parasal değerler string (backend'de Decimal) — float YASAK.
 */

export const PURCHASE_CATEGORIES = ['SUB_CONSULTANT', 'MATERIAL', 'SERVICE', 'EQUIPMENT', 'OTHER'] as const
export const PURCHASE_PRICING_MODES = ['UNIT_PRICE', 'LUMP_SUM'] as const

/** Birim fiyat / tutar — 2 ondalık, string */
const priceString = z
  .string({ required_error: 'Fiyat zorunludur.' })
  .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir fiyat giriniz.')

// ─── Teklif Talebi (RFQ) ──────────────────────────────────────────────────────

const RequestItemSchema = z.object({
  pozNo: z.string().max(50).nullable().optional(),
  description: z.string({ required_error: 'Kalem açıklaması zorunludur.' }).min(1).max(1000),
  unit: z.string().max(30).default('UNIT'),
  /** Miktar — 4 ondalığa kadar */
  quantity: z.number({ required_error: 'Miktar zorunludur.' }).min(0).default(0),
  /** Bütçe/tahmini birim fiyat (opsiyonel referans) */
  estimatedUnitPrice: priceString.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
})

export const CreatePurchaseRequestSchema = z.object({
  title: z.string({ required_error: 'Başlık zorunludur.' }).min(3, 'Başlık en az 3 karakter olmalıdır.').max(200),
  description: z.string().max(2000).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  category: z.enum(PURCHASE_CATEGORIES).default('OTHER'),
  currency: z.nativeEnum(Currency).default(Currency.TRY),
  pricingMode: z.enum(PURCHASE_PRICING_MODES).default('UNIT_PRICE'),
  deadline: z.string().date().nullable().optional(),
  items: z.array(RequestItemSchema).default([]),
})

export const UpdatePurchaseRequestSchema = z.object({
  title: z.string().min(3).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  category: z.enum(PURCHASE_CATEGORIES).optional(),
  currency: z.nativeEnum(Currency).optional(),
  pricingMode: z.enum(PURCHASE_PRICING_MODES).optional(),
  deadline: z.string().date().nullable().optional(),
  // Durum geçişi: DRAFT ↔ OPEN ↔ CANCELLED (AWARDED yalnız award endpoint'iyle)
  status: z.enum(['DRAFT', 'OPEN', 'CANCELLED']).optional(),
})

/** İstenen kalemleri toplu değiştirir (İstenen Kalemler sekmesi) */
export const ReplaceRequestItemsSchema = z.object({
  items: z.array(RequestItemSchema),
})

// ─── Tedarikçi Teklifi (Bid) ──────────────────────────────────────────────────

const BidItemSchema = z.object({
  requestItemId: z.string().uuid(),
  unitPrice: priceString,
  notes: z.string().max(300).nullable().optional(),
})

export const CreateVendorBidSchema = z.object({
  vendorId: z.string({ required_error: 'Tedarikçi seçimi zorunludur.' }).uuid(),
  bidDate: z.string().date().nullable().optional(),
  validUntil: z.string().date().nullable().optional(),
  deliveryDays: z.number().int().min(0).nullable().optional(),
  paymentTerms: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  attachmentUrl: z.string().nullable().optional(),
  attachmentName: z.string().max(255).nullable().optional(),
  /** LUMP_SUM modunda elle girilen toplam; UNIT_PRICE modunda kalemlerden hesaplanır */
  totalAmount: priceString.optional(),
  items: z.array(BidItemSchema).default([]),
})

export const UpdateVendorBidSchema = z.object({
  bidDate: z.string().date().nullable().optional(),
  validUntil: z.string().date().nullable().optional(),
  deliveryDays: z.number().int().min(0).nullable().optional(),
  paymentTerms: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  attachmentUrl: z.string().nullable().optional(),
  attachmentName: z.string().max(255).nullable().optional(),
  status: z.enum(['RECEIVED', 'SHORTLISTED', 'REJECTED']).optional(),
  totalAmount: priceString.optional(),
  items: z.array(BidItemSchema).optional(),
})

// ─── Karara Bağlama (Award) ───────────────────────────────────────────────────

export const AwardBidSchema = z.object({
  bidId: z.string({ required_error: 'Teklif seçimi zorunludur.' }).uuid(),
  /** Kazananı neye dönüştürelim */
  convertTo: z.enum(['CONTRACT', 'PURCHASE', 'NONE']).default('NONE'),
  /** CONTRACT için proje zorunlu (talepte proje yoksa burada seçilir) */
  projectId: z.string().uuid().nullable().optional(),
})

export type CreatePurchaseRequestInput = z.infer<typeof CreatePurchaseRequestSchema>
export type UpdatePurchaseRequestInput = z.infer<typeof UpdatePurchaseRequestSchema>
export type ReplaceRequestItemsInput = z.infer<typeof ReplaceRequestItemsSchema>
export type CreateVendorBidInput = z.infer<typeof CreateVendorBidSchema>
export type UpdateVendorBidInput = z.infer<typeof UpdateVendorBidSchema>
export type AwardBidInput = z.infer<typeof AwardBidSchema>

/** Listede olmayan tedarikçiyi hızlı ekleme (`POST /procurement/vendors`) */
export const QuickVendorSchema = z.object({
  name: z.string({ required_error: 'Tedarikçi adı zorunludur.' }).trim().min(2, 'Tedarikçi adı zorunludur.').max(200),
})

// Form girdi tipleri (varsayılanlar zod tarafından doldurulur; istemci bunları göndermek zorunda değildir)
export type PurchaseRequestFormInput = z.input<typeof CreatePurchaseRequestSchema>
export type ReplaceRequestItemsFormInput = z.input<typeof ReplaceRequestItemsSchema>
export type VendorBidFormInput = z.input<typeof CreateVendorBidSchema>
export type UpdateVendorBidFormInput = z.input<typeof UpdateVendorBidSchema>
export type AwardBidFormInput = z.input<typeof AwardBidSchema>
export type QuickVendorInput = z.infer<typeof QuickVendorSchema>
